import { useCallback, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import {
  IGetDictionaryNamedCohortsQuery,
  IGetDictionaryNamedCohortsQueryVariables,
  ISearchSchedulesQuery,
  ISearchSchedulesQueryVariables,
  PermissionKey,
  ScheduleType,
} from '../../../utils/apollo/graphqlSchema';
import SEARCH_SCHEDULES from '../../Schedule/apollo/SEARCH_SCHEDULES';
import usePermission from '../../../utils/hooks/permissionHooks/usePermission';
import { GET_DICTIONARY_NAMED_COHORTS } from '../../../utils/apollo/cohorts';
import { useAppDispatch } from '../../../redux/hooks';
import { setDictionaryListAction } from '../../../redux/slices/dictionary';
import { DictionaryTypeEnum } from '../../../utils/interfaces';
import { clearSchedulesAction } from '../../../redux/actions/scheduler';

interface IProps {
  schemaId: string;
  searchTerm: string;
}
export default ({ schemaId, searchTerm }: IProps) => {
  const dispatch = useAppDispatch();
  const { checkIsPermitted } = usePermission();
  const isPermittedToReadBrazeExport = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.CohortBrazeExportRead],
    schemaId,
  });

  const isPermittedToReadCohorts = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.CohortRead],
    schemaId,
  });
  const onFetchSchedules = useCallback(
    ({ searchSchedules }: ISearchSchedulesQuery) => {
      dispatch(
        setDictionaryListAction({
          newValues: searchSchedules?.entities ?? [],
          dictionaryType: DictionaryTypeEnum.ScheduleCohort,
        }),
      );
    },
    [dispatch],
  );

  const { loading: isSchedulesLoading } = useQuery<
    ISearchSchedulesQuery,
    ISearchSchedulesQueryVariables
  >(SEARCH_SCHEDULES, {
    variables: {
      term: searchTerm,
      schemaKey: schemaId,
      includes: [],
      types: [ScheduleType.BrazeCohortImport],
      pagination: { after: 0, first: 0 },
    },
    onCompleted: onFetchSchedules,
    // schedule is display based on ReadCohort, therefore need to check both
    skip: !isPermittedToReadBrazeExport || !isPermittedToReadCohorts,
  });

  const onFetchCohorts = useCallback(
    ({ namedCohorts }: IGetDictionaryNamedCohortsQuery) => {
      dispatch(
        setDictionaryListAction({
          newValues: namedCohorts.entities,
          dictionaryType: DictionaryTypeEnum.DictionaryCohort,
        }),
      );
    },
    [dispatch],
  );
  const { loading: isNamedCohortsLoading } = useQuery<
    IGetDictionaryNamedCohortsQuery,
    IGetDictionaryNamedCohortsQueryVariables
  >(GET_DICTIONARY_NAMED_COHORTS, {
    variables: {
      term: searchTerm,
      includes: [],
      schemaId: searchTerm?.length === 0 ? schemaId : null,
      includeCompound: true,
      pagination: { after: 0, first: 0 },
    },
    onCompleted: onFetchCohorts,
    skip: !isPermittedToReadCohorts,
  });

  useEffect(() => {
    return () => {
      dispatch(clearSchedulesAction());
    };
  }, [dispatch]);

  return isNamedCohortsLoading || isSchedulesLoading;
};
